/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useMemo, useState } from 'react';
import { TableColumnForm } from './partials/TableColumnForm';
import { Button } from 'reactstrap';
import type { InputType } from 'reactstrap/types/lib/Input';
import { addMetricToGroup, createMetricGroup } from '../../actions/initiative';
import { SDGGoal, sdgMap, SDGTarget } from '../../constants/sdg-data';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import {
  UniversalTrackerBlueprintMin,
  UtrValueType,
  ValueValidation,
  TableColumn,
} from '@g17eco/types/universalTracker';
import { FormGenerator } from '@g17eco/molecules/form';
import { Loader } from '@g17eco/atoms/loader';
import SDGPicker from '@features/sdg-picker';
import LanguageFormContainer from './partials/LanguageFormContainer';
import {
  canSetNumberScale,
  canSetUnitType,
  hasInvalidAlternativeFields,
  hasMinAndMax,
  hasPassedValidation,
  isValidMinMaxData,
  isValidTableConfig,
  validateTypeCode,
} from './utils';
import { QUESTION, SURVEY } from '@constants/terminology';
import { ValueTypeSettingsContainer } from './partials/ValueTypeSettingsContainer';
import {
  useCheckIsCustomMetricAnsweredQuery,
  useGetInitiativeCustomMetricsQuery,
  useCreateCustomMetricMutation,
  useUpdateCustomMetricMutation,
  useDeleteCustomMetricMutation,
} from '@api/custom-metrics';
import { CANNOT_EDIT_NOTE } from './constants';
import classNames from 'classnames';
import { Field, Form, FormProps, CustomMetricPayload } from './types';
import { DashboardSection } from '@g17eco/molecules/dashboard';

const getGroupAFields = (): Field[] => [
  {
    code: 'name',
    type: 'text',
    required: true,
    label: `${QUESTION.CAPITALIZED_SINGULAR} title`,
    placeholder: `e,g. Supplier living wage - (this is what appears on the ${SURVEY.CAPITALIZED_SINGULAR} overview page)`,
    classes: {
      label: 'fw-bold',
    },
    testId: 'custom-metric-name-input',
  },
  {
    code: 'valueLabel',
    type: 'text',
    required: true,
    label: `${QUESTION.CAPITALIZED_SINGULAR} text`,
    placeholder: `e,g. What is your supplier living wage? - (this is text of the actual ${QUESTION.SINGULAR})`,
    classes: {
      label: 'fw-bold',
    },
    testId: 'custom-metric-valueLabel-input',
  },
  {
    code: 'typeCode',
    type: 'text',
    required: false,
    label: `${QUESTION.CAPITALIZED_SINGULAR} code (max 15 characters)`,
    tooltip: 'Max 15 characters, and cannot include spaces',
    placeholder: 'e,g. pack1-q3',
    classes: {
      label: 'fw-bold',
    },
    isValid: validateTypeCode,
    testId: 'custom-metric-typeCode-input',
  },
  {
    code: 'instructions',
    type: 'textarea',
    label: `${QUESTION.CAPITALIZED_SINGULAR} instructions`,
    placeholder: `If there are any special instructions or tips on answering this ${QUESTION.SINGULAR}, you can add them here`,
    classes: {
      label: 'fw-bold',
    },
    testId: 'custom-metric-instructions-input',
  },
];

export const getInitialState: () => Form = () => ({
  errorMessage: '',
  name: '',
  valueLabel: '',
  typeCode: '',
  valueType: '',
  tags: {
    sdg: [],
  },
  valueValidation: {},
  submitting: false,
});

const getGroupBFields = (isAnswered: boolean): Field[] => [
  {
    code: 'valueType',
    type: 'select' as InputType,
    required: true,
    disabled: isAnswered,
    label: 'Value Type',
    options: [
      { value: 'text', label: 'Text' },
      { value: 'number', label: 'Number' },
      { value: 'percentage', label: 'Percentage' },
      { value: 'valueList', label: 'Yes/No' },
      { value: 'date', label: 'Date' },
      { value: 'table', label: 'Table' },
    ],
    classes: {
      label: 'fw-bold',
    },
    note: isAnswered ? CANNOT_EDIT_NOTE : undefined,
    testId: 'metric-value-type-select',
  },
];

export interface CreateCustomMetricPageProps extends Omit<FormProps, 'customMetricsUsage'> {
  initiativeId: string;
  questions: UniversalTrackerBlueprintMin[];
  isAddingToGroup?: boolean;
  isReadOnly?: boolean;
  metricId?: string;
  onNavigateBack?: (initiativeId: string) => void;
}

export const CreateCustomMetricPage = ({
  initiativeId,
  questions,
  handleReload,
  metricGroup,
  isAddingToGroup = false,
  isReadOnly = false,
  metricId,
  onNavigateBack,
}: CreateCustomMetricPageProps) => {
  const [formData, setFormData] = useState<Form>(getInitialState());

  const [activeColumnCode, setActiveColumnCode] = useState<string | null>(null);
  const [isAddingColumn, setIsAddingColumn] = useState(false);

  const { data: customMetrics = { created: [], used: [] } } = useGetInitiativeCustomMetricsQuery(initiativeId, {
    skip: !initiativeId,
  });

  const { data: { isAnswered } = { isAnswered: true } } = useCheckIsCustomMetricAnsweredQuery(
    { initiativeId, utrId: metricId ?? '' },
    { skip: !initiativeId || !metricId },
  );

  const [createCustomMetric, { isLoading: isCreating }] = useCreateCustomMetricMutation();
  const [updateCustomMetric, { isLoading: isUpdating }] = useUpdateCustomMetricMutation();
  const [deleteCustomMetric, { isLoading: isDeleting }] = useDeleteCustomMetricMutation();

  const isSaving = isCreating || isUpdating || isDeleting;

  // Edit mode is determined by whether metricId was provided (not by formData._id which loads asynchronously)
  const isEditMode = !!metricId;

  // Look for the metric in questions first (for new metrics), then in custom metrics (for editing)
  let question = questions.find((q) => q._id === metricId);
  if (!question && customMetrics) {
    const allCustomMetrics = [...(customMetrics.created ?? []), ...(customMetrics.used ?? [])];
    question = allCustomMetrics.find((q) => q._id === metricId);
  }

  const groupAFields = getGroupAFields();
  const groupBFields = useMemo(() => getGroupBFields(isAnswered && isEditMode), [isAnswered, isEditMode]);
  const combinedMetricFields = [...groupAFields, ...groupBFields];
  const isValid = (() => {
    const hasEmptyRequiredField = combinedMetricFields.some((field) => field.required && !formData[field.code]);
    if (hasEmptyRequiredField) {
      return false;
    }
    const hasInvalidAlternatives = hasInvalidAlternativeFields({
      formFields: groupAFields,
      alternatives: formData.alternatives,
    });
    const passedValidation = hasPassedValidation({ formFields: groupAFields, formData });

    const hasValidMinMax = isValidMinMaxData(formData.valueValidation);
    const hasValidTableConfig = isValidTableConfig(formData.valueType, formData.valueValidation);

    return !hasInvalidAlternatives && passedValidation && hasValidMinMax && hasValidTableConfig;
  })();

  // Fields to display in form
  const groupBFilteredFields = !isEditMode
    ? groupBFields
    : groupBFields.filter((f) => {
        if (f.code === 'valueType') {
          // Check if we should display valueType, only for currently supported types
          return f.options?.some((option) => option.value === formData.valueType);
        }
        return true;
      });

  const onChange = (e: React.ChangeEvent<{ name: string; value: string | number | undefined }>) => {
    const name = e.target.name;
    const value = e.target.value;
    const newFormData = { ...formData, errorMessage: '', [name]: value };
    if (!canSetUnitType(newFormData)) {
      newFormData.unitType = '';
    }

    if (!canSetNumberScale(newFormData)) {
      newFormData.numberScale = '';
    }

    setFormData((prev) => ({ ...prev, ...newFormData }));
  };

  const setSDGCodes = (codes: string[]) => {
    const newFormData: Form = {
      ...formData,
      errorMessage: '',
      tags: {
        sdg: codes,
      },
    };

    setFormData(newFormData);
  };

  const setValueValidation = (value: ValueValidation) => {
    const hasMinAndMaxValue = hasMinAndMax(value);
    setFormData((prev) => ({
      ...prev,
      numberScale: hasMinAndMaxValue ? '' : prev.numberScale,
      valueValidation: {
        ...prev.valueValidation,
        ...value,
      },
    }));
  };

  const handleSaveColumn = (column: TableColumn) => {
    const columns = formData.valueValidation?.table?.columns ? [...formData.valueValidation.table.columns] : [];

    // Uniqueness check
    if (activeColumnCode !== column.code && columns.some((c) => c.code === column.code)) {
      alert(`The code "${column.code}" is already used by another column. Please use a unique code.`);
      return;
    }

    const existingIndex = columns.findIndex((c) => c.code === activeColumnCode);

    if (activeColumnCode && existingIndex !== -1) {
      columns[existingIndex] = column;
    } else {
      columns.push(column);
    }

    setValueValidation({
      ...formData.valueValidation,
      table: {
        ...formData.valueValidation?.table,
        columns,
        validation:
          formData.valueValidation?.table?.validation?.maxRows !== undefined
            ? { maxRows: formData.valueValidation.table.validation.maxRows }
            : undefined,
      },
    });

    setIsAddingColumn(false);
    setActiveColumnCode(null);
  };

  const handleCancelColumnForm = () => {
    setIsAddingColumn(false);
    setActiveColumnCode(null);
  };

  const handleAddColumnTrigger = () => {
    setIsAddingColumn(true);
  };

  const handleEditColumnTrigger = (code: string) => {
    setActiveColumnCode(code);
  };

  const handleCancelMetric = () => {
    setFormData(getInitialState());
    if (onNavigateBack) {
      onNavigateBack(initiativeId);
    }
  };

  const handleDeleteMetric = async (utrId: string) => {
    const actionConfirmation = window.confirm(
      'Are you sure you want to delete this Metric? All data associated with Metric will be lost and cannot be recovered.',
    );
    if (!actionConfirmation) {
      return;
    }

    return deleteCustomMetric({ initiativeId, utrId })
      .unwrap()
      .then(() => {
        generateToast({
          color: 'success',
          title: 'Success',
          message: 'Custom metric has been removed',
        });
        handleReload();
        handleCancelMetric();
      })
      .catch((e) => {
        toastFromError({
          error: e,
          title: 'Error deleting metric',
        });
      });
  };

  const handleCreateOrUpdateMetric = async () => {
    const utrData: CustomMetricPayload = {
      name: formData.name,
      valueLabel: formData.valueLabel,
      typeCode: formData.typeCode,
      instructions: formData.instructions,
      valueType: formData.valueType,
      unitType: formData.unitType,
      numberScale: formData.numberScale,
      tags: formData.tags,
      alternatives: formData.alternatives,
      valueValidation: formData.valueValidation,
    };

    if (formData._id) {
      return updateCustomMetric({ initiativeId, utrId: formData._id, formData: utrData })
        .unwrap()
        .then(() => {
          generateToast({
            color: 'success',
            title: 'Success',
            message: 'Custom metric has been updated',
          });
          handleReload();
          handleCancelMetric();
        })
        .catch((e) => {
          toastFromError({
            error: e,
            title: 'Error updating metric',
          });
        });
    }

    try {
      const utr = await createCustomMetric({ initiativeId, formData: utrData }).unwrap();

      if (!isAddingToGroup) {
        handleCancelMetric();
        return;
      }

      let groupId;
      if (metricGroup?._id) {
        groupId = metricGroup._id;
      } else {
        const groupData = await createMetricGroup(initiativeId, metricGroup);
        groupId = groupData._id;
      }

      const analytics = getAnalytics();
      await analytics.track(AnalyticsEvents.CustomMetricCreated, {
        initiativeId,
        utrId: utr._id,
        utrCode: utr.code,
        utrValueLabel: utr.valueLabel,
        groupId: groupId,
      });

      await addMetricToGroup(initiativeId, groupId, utr._id);
      generateToast({
        color: 'success',
        title: 'Success',
        message: 'Custom metric has been created',
      });
    } catch (e) {
      toastFromError({
        error: e,
        title: 'Error saving metric',
      });
    } finally {
      handleReload();
      handleCancelMetric();
    }
  };

  React.useEffect(() => {
    if (formData.valueType === UtrValueType.Percentage && !formData._id) {
      setValueValidation({
        min: 0,
        max: 100,
      });
    }
  }, [formData._id, formData.valueType]);

  React.useEffect(() => {
    if (!question) {
      return;
    }

    setFormData((f) => {
      const data: Form = {
        ...f,
        _id: question._id,
        name: question.name ?? '',
        valueLabel: question.valueLabel ?? '',
        instructions: question.instructions ?? '',
        typeCode: question.typeCode ?? '',
        valueType: question.valueType ?? '',
        unitType: question.unitType ?? '',
        numberScale: question.numberScale ?? '',
        alternatives: question.alternatives,
        valueValidation: question.valueValidation ?? {},
        tags: {
          sdg: [],
        },
      };

      const sdg = question?.tags?.sdg;
      if (sdg) {
        sdgMap.forEach((goal: SDGGoal) => {
          if (sdg?.includes(goal.code)) {
            data.tags.sdg.push(goal.code);
            goal.targets.forEach((target: SDGTarget) => {
              if (!target.hidden && sdg?.includes(target.code)) {
                data.tags.sdg.push(target.code);
              }
            });
          }
        });
      }
      return data;
    });
  }, [metricId, question, questions, customMetrics]);

  if (isAddingColumn || activeColumnCode !== null) {
    const initialColumnData =
      activeColumnCode !== null
        ? formData.valueValidation?.table?.columns?.find((c: TableColumn) => c.code === activeColumnCode)
        : undefined;

    return (
      <DashboardSection>
        <TableColumnForm
          initialData={initialColumnData}
          onSave={handleSaveColumn}
          onCancel={handleCancelColumnForm}
          existingCodes={formData.valueValidation?.table?.columns?.map((c: TableColumn) => c.code) ?? []}
          readOnly={isAnswered && isEditMode}
          initiativeId={initiativeId}
        />
      </DashboardSection>
    );
  }

  return (
    <>
      <div className='col-12 px-3 mb-4'>
        <div className='d-flex justify-content-between align-items-center'>
          <h3 className='mb-0'>{isEditMode ? 'Edit Custom Metric' : 'Create a Custom Metric'}</h3>
        </div>
      </div>
      <DashboardSection>
        <div className='position-relative'>
          {isSaving && <Loader />}
          <div className={classNames({ isSaving: isSaving })}>
            <div className={classNames({ 'pe-none': isReadOnly })}>
              <FormGenerator fields={groupAFields} form={formData} updateForm={onChange} />
              {isReadOnly ? null : <LanguageFormContainer formData={formData} onChange={onChange} />}
              <FormGenerator fields={groupBFilteredFields} form={formData} updateForm={onChange} />
              <ValueTypeSettingsContainer
                initialData={question}
                formData={formData}
                onChange={onChange}
                setValueValidation={setValueValidation}
                isAnswered={isAnswered && isEditMode}
                onAddColumn={handleAddColumnTrigger}
                onEditColumn={handleEditColumnTrigger}
              />
              <SDGPicker
                handleUpdate={setSDGCodes}
                selectedCodes={formData.tags?.sdg ?? []}
                includeTargets={true}
                classes={{ label: 'fw-bold' }}
              />
            </div>

            <div className='d-flex justify-content-between mt-4 pt-3'>
              <div>
                <Button
                  disabled={!isEditMode || isAnswered || isReadOnly}
                  color='danger'
                  className='mr-2'
                  onClick={() => handleDeleteMetric(formData?._id ?? '')}
                >
                  Delete
                </Button>
              </div>
              <div>
                <Button color='link-secondary' onClick={handleCancelMetric} className='mr-3'>
                  Cancel
                </Button>
                <Button
                  disabled={!isValid || isReadOnly}
                  color='primary'
                  className='ml-3'
                  onClick={handleCreateOrUpdateMetric}
                  data-testid='custom-metric-form-submit-btn'
                >
                  {isEditMode ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DashboardSection>
    </>
  );
};
